import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdDelete, MdOutlineModeEdit, MdRemoveRedEye } from "react-icons/md";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PopUpForm from "../../components/PopUpForm";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import { useApi } from "../../services/api";
import { LocalizedText, TeamMember } from "../../services/generated/client";
import {
  createTeamMember,
  getAllTeamMembers,
  removeTeamMember,
  updateTeamMember,
} from "../../services/team/teamServices";
import styles from "./styles.module.css";

const Team = () => {
  const { withErrorHandling } = useApi();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember>({
    _id: {},
    fullName: localizedTextDefaultValue,
    position: localizedTextDefaultValue,
    description: localizedTextDefaultValue,
    imageUrl: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState(false);
  const [languageEditing, setLanguageEditing] =
    useState<keyof LocalizedText>("ka");

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    withErrorHandling(async () => {
      const response = await getAllTeamMembers();
      if (response) setTeamMembers(response);
    });
  }, []);

  const handleEdit = (member: TeamMember) => {
    setSelectedMember(member);
    setIsAdding(false);
    setView(false);
    setIsModalOpen(true);
  };

  const handleView = (member: TeamMember) => {
    setSelectedMember(member);
    setIsAdding(false);
    setView(true);
    setIsModalOpen(true);
  };

  const handleDelete = useCallback(
    async (id: string, fullName: string) => {
      if (window.confirm(`Are you sure you want to delete ${fullName}?`)) {
        await withErrorHandling(async () => {
          await removeTeamMember(id);
          setTeamMembers((prev) =>
            prev.filter((member) => member._id.toString() !== id)
          );
        });
      }
    },
    [withErrorHandling]
  );

  const handleUpdateMember = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedMember) return;
    const { _id, fullName, position, description, imageUrl } = selectedMember;

    await withErrorHandling(async () => {
      if (isAdding) {
        const newMember = await createTeamMember({
          fullName,
          position,
          description,
          imageUrl,
        });
        if (newMember) {
          setTeamMembers((prev) => [...prev, newMember]);
        }
      } else {
        await updateTeamMember(_id.toString(), {
          fullName,
          position,
          description,
          imageUrl,
        });
        setTeamMembers((prev) =>
          prev.map((member) => (member._id === _id ? selectedMember : member))
        );
      }
      setIsModalOpen(false);
    });
  };

  const handleAddMember = () => {
    setSelectedMember({
      _id: {},
      fullName: localizedTextDefaultValue,
      position: localizedTextDefaultValue,
      description: localizedTextDefaultValue,
      imageUrl: "",
    });
    setIsAdding(true);
    setIsModalOpen(true);
  };

  const columns = useMemo<MRT_ColumnDef<TeamMember>[]>(
    () => [
      {
        accessorKey: "fullName",
        header: "Full Name",
        Cell: ({ row }) => row.original.fullName?.[languageEditing] || "",
      },
      {
        accessorKey: "position",
        header: "Position",
        Cell: ({ row }) => row.original.position?.[languageEditing] || "",
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ row }) => row.original.description?.[languageEditing] || "",
      },
      {
        header: "Actions",
        Cell: ({ row }: { row: { original: TeamMember } }) => (
          <div className={styles.actionBtns}>
            <button
              onClick={() => handleView(row.original)}
              className={styles.tableBtn}
            >
              <MdRemoveRedEye />
            </button>
            <button
              onClick={() => handleEdit(row.original)}
              className={styles.tableBtn}
            >
              <MdOutlineModeEdit />
            </button>
            <button
              onClick={() =>
                handleDelete(
                  row.original._id.toString(),
                  row.original.fullName.en
                )
              }
              className={`${styles.tableBtn} ${styles.delete}`}
            >
              <MdDelete />
            </button>
          </div>
        ),
      },
    ],
    [handleDelete, languageEditing]
  );

  const handleMultiLanguageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id as keyof TeamMember;
    const value = e.target.value;

    setSelectedMember((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] as LocalizedText),
        [languageEditing]: value,
      },
    }));
  };

  return (
    <div className={styles.mainContent}>
      <h2>Manage Team Members</h2>
      <div className={styles.tableWrapper}>
        <MaterialReactTable
          columns={columns}
          data={teamMembers}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", gap: "20px" }}>
              <button className={styles.addBtn} onClick={handleAddMember}>
                + Add Member
              </button>
              <div>
                <LanguageSwitcher
                  languageEditing={languageEditing}
                  setLanguageEditing={setLanguageEditing}
                  inForm={false}
                />
              </div>
            </div>
          )}
          muiTableContainerProps={{
            sx: {
              flex: 1,
              overflowY: "hidden",
            },
          }}
          muiTablePaperProps={{
            sx: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableBodyProps={{
            sx: {
              overflowY: "auto",
              height: "100%",
            },
          }}
          muiTableProps={{
            sx: {
              maxHeight: "100%",
              gridTemplateRows: "auto 1fr",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              overflow: "auto",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          layoutMode="grid"
        />
      </div>

      {isModalOpen && selectedMember && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} ref={modalRef}>
            <button
              className={styles.closeButton}
              onClick={() => setIsModalOpen(false)}
            >
              X
            </button>
            <LanguageSwitcher
              languageEditing={languageEditing}
              setLanguageEditing={setLanguageEditing}
              inForm={true}
            />
            <PopUpForm
              title={"Team Member"}
              submitLabel={isAdding ? "Create" : "Update"}
              onSubmit={handleUpdateMember}
              isView={view}
              languageEditing={languageEditing}
              isAdding={isAdding}
              fields={[
                {
                  id: "imageUrl",
                  label: "Image URL",
                  type: "imageUrl",
                  value: selectedMember.imageUrl || "",
                  onImageChange: (_, url) =>
                    setSelectedMember((prev) => ({
                      ...prev,
                      imageUrl: url,
                    })),
                },
                {
                  id: "fullName",
                  label: `Full Name ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedMember.fullName?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "position",
                  label: `Position ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedMember.position?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "description",
                  label: `Description ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedMember.description?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
